/**
 * 幕二 · 分镜条(矮卡)—— 左栏只负责「全片长什么样、我在第几镜」(2026-08-06 幕二重设计)。
 *
 * ═══ 为什么从 769px 压到 ~150px ═══════════════════════════════════════════════
 * 真机实测(5 镜真实数据):页面总高 3112px、单张分镜卡 **769px** —— 一张卡快占满一屏,
 * 里面塞了镜号/星标/三态/节拍/秒数/删/预览图/重出/上传/6 个镜头语言选择器/环境/动作/
 * 口播+密度条/负面词。而每次真要改的只有环境、动作、口播三样,那 6 个选择器绝大多数时候
 * 是「AI 定」不动,却占着固定版面。
 *
 * 现在:左栏 = 导航(看全片 + 选一镜),右栏 = 工作台(改这一镜)。矮卡只留**看得懂所必需**的:
 * 缩略图 / 镜号+时长 / 一句「会画成」/ 镜头语言摘要 / 状态角标。
 *
 * ★状态角标不能省:一张台本图到底「会用于出片」还是「只给人看」,是用户决定要不要重出的依据。
 *   T2V 镜的图不参与渲染(批 B2 定的两个字段),含糊成"首帧"二字就是概览比实际乐观。
 */
import { Star } from "lucide-react";

import { ratioToCss } from "@/shared/lib/video/aspect";
import { VOICEOVER_DENSITY_MAX, voiceoverDensity } from "@/shared/lib/video/cameraLexicon";
import { cn } from "@/shared/lib/utils";
import { shotSummary } from "../../model/shotSummary";
import { lensDigest } from "../../model/shotSummary";
import type { PanelShotData } from "../../model/panelShot";
import { shotFactChipLabel } from "../../model/factChip";

interface Props {
  shots: PanelShotData[];
  ratio: string;
  /** 当前选中的镜(null = 没选,右栏显示全片视图)。 */
  selected: number | null;
  onSelect: (index: number) => void;
  onAddShot?: () => void;
  /** 每镜所属节拍名(蓝图路);缺省不显示。 */
  beatLabels?: (string | null)[];
  /** W4:F 码 → 卖点原文(逐镜绿标取文用);缺映射时绿标退回显示 fid 本身,不整段隐藏。 */
  factTexts?: Record<string, string> | null;
  /** M2 结构轨 ↔ 分镜的**双向** hover 高亮(蓝图路);缺省不联动。 */
  beatHighlights?: boolean[];
  onBeatHover?: (shotIndex: number, hover: boolean) => void;
  /**
   * 每镜都出台本图。**只认显式 `true`**;false / null / 缺省一律 = 只给要当起始画面的镜出图(2026-08-09 默认)。
   * 这里**只读不改** —— 开关在备料台④,因为它只在编排前有意义(批量出图那一跳读它)。这里如实说当前是哪种口径。
   */
  previewFrames?: boolean | null;
  disabled?: boolean;
}

export function VideoShotStrip({
  shots,
  ratio,
  selected,
  onSelect,
  onAddShot,
  beatLabels,
  beatHighlights,
  onBeatHover,
  previewFrames,
  disabled,
  factTexts,
}: Props) {
  return (
    <div data-video-shot-strip className={cn("space-y-2.5", disabled && "opacity-60")}>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <span className="text-[13px] font-medium">分镜脚本（{shots.length} 镜）</span>
        <div className="flex items-center gap-2">
          {/* ★2026-08-09:判据从 `=== false` 改成 `!== true`。默认翻面后绝大多数单子的
              previewFrames 是 undefined(用户没碰过那个勾)—— 行为已经是只出起始帧,而这句解释
              按老判据不显示,用户看到一排没有图的镜、没有任何说明。界面说的必须等于实际做的。 */}
          {/* ★1005 生产实测:3 镜全是文生镜时一张台本图都不出、也不扣台本图费,旧句
              「只有会用于出片的镜有图」被读成「这 3 镜都该有图」。全文生时如实说没出、没扣。 */}
          {previewFrames !== true && (
            <span
              data-testid="shot-strip-frame-note"
              className="rounded-full border border-border/60 px-2 py-0.5 text-2xs text-muted-foreground"
            >
              {shots.length > 0 && shots.every((s) => s.shotMode === "T2V")
                ? "各镜均按文生出片，不需要起始画面：未出台本图、未扣台本图费（想每镜都看图，去备料台勾上）"
                : "只有要当起始画面的镜（图生镜）有图（想每镜都出，去备料台勾上）"}
            </span>
          )}
          <span className="text-2xs text-muted-foreground">点一镜 → 右栏改它</span>
        </div>
      </div>
      <div
        className={cn(
          "grid grid-cols-[repeat(auto-fill,minmax(196px,1fr))] gap-2.5",
          disabled && "pointer-events-none"
        )}
      >
        {shots.map((s, i) => {
          const shown = s.firstFrameUrl || s.previewUrl;
          // 批 B2 两个字段两种身份:有渲染键 = 这张就是出片的起始画面;否则只是给人看的台本图。
          const isRenderFrame = !!s.firstFrameKey;
          const over = voiceoverDensity(s.voiceover, s.duration) > VOICEOVER_DENSITY_MAX;
          const summary = shotSummary({ scene: s.scene, motion: s.motion });
          const lens = lensDigest(s);
          const factChip = shotFactChipLabel(s.factRef, factTexts);
          const on = selected === i;
          return (
            <button
              key={i}
              type="button"
              aria-pressed={on}
              data-testid={`shot-card-${i}`}
              /* 结构轨 hover 到某一拍时,属于那一拍的镜亮起来。做成属性而不是只靠 class ——
               判据要钉在「这一镜被点名了」这件事上,而不是钉在某个 tailwind 类名上。 */
              data-highlight={beatHighlights?.[i] ? "true" : undefined}
              onClick={() => onSelect(i)}
              onMouseEnter={onBeatHover ? () => onBeatHover(i, true) : undefined}
              onMouseLeave={onBeatHover ? () => onBeatHover(i, false) : undefined}
              className={cn(
                "cursor-pointer overflow-hidden rounded-[10px] border text-left transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                on
                  ? "border-primary shadow-[0_0_0_1px_hsl(var(--primary))]"
                  : beatHighlights?.[i]
                    ? "border-primary/60 bg-primary/5"
                    : "border-border/60 hover:border-primary/50"
              )}
            >
              {/**
               * ★媒体框**按高度定宽**,不按宽度定高(2026-08-06 真浏览器实测修)。
               * 原来写的是 `w-full` + aspectRatio:9:16 竖版在 ~200px 列宽下算出 ~350px 高,
               * 整卡 **456px** —— 「矮卡」在竖版下根本不矮,而竖版正是这条产品线的主力比例。
               * 现在外框固定 124px 高,内层由高度推宽度:竖版得到一枚窄缩略图居中,
               * 横版被 max-w-full 收在列内。卡高因此与比例无关。
               */}
              <div className="relative flex h-[124px] items-center justify-center overflow-hidden bg-muted/40">
                {shown ? (
                  <img
                    src={shown}
                    alt={`镜 ${i + 1} ${isRenderFrame ? "起始画面" : "台本预览图"}`}
                    className="h-full max-w-full object-cover"
                    style={{ aspectRatio: ratioToCss(ratio, "16 / 9") }}
                  />
                ) : (
                  <span className="text-2xs text-muted-foreground">暂无台本图</span>
                )}
                {/* ★身份角标:出片会用 vs 只给人看 —— 这是用户决定要不要重出的依据 */}
                <span
                  className={cn(
                    "absolute left-1.5 top-1.5 rounded-full border px-1.5 py-px text-2xs backdrop-blur-sm",
                    isRenderFrame
                      ? "border-amber-400/60 bg-background/75 text-amber-600 dark:text-amber-400"
                      : "border-border/60 bg-background/75 text-muted-foreground"
                  )}
                >
                  {isRenderFrame ? "起始画面 · 会用于出片" : "仅预览"}
                </span>
                {s.heroMoment && (
                  <Star
                    className="absolute right-1.5 top-1.5 h-3.5 w-3.5 fill-current text-amber-500"
                    aria-label="产品镜"
                  />
                )}
              </div>
              <div className="px-2 py-1.5">
                <div className="mb-0.5 flex flex-wrap items-center gap-x-1.5 text-2xs text-muted-foreground">
                  <b className="text-[12px] font-semibold text-foreground">镜 {i + 1}</b>
                  <span>· {s.duration}s</span>
                  {over && <span className="text-amber-600 dark:text-amber-400">· 口播超密</span>}
                  {beatLabels?.[i] && (
                    <span className="max-w-full truncate text-primary/80">{beatLabels[i]}</span>
                  )}
                </div>
                <p className="line-clamp-2 text-2xs leading-snug text-foreground/85">
                  {summary || <span className="text-muted-foreground">还没写内容</span>}
                </p>
                {/* 镜头意图(2026-08-10 导演台本上屏):导演产的「这镜为什么存在」——
                    此前解析进了 shot 模型却零渲染,用户对着一排画面猜结构。 */}
                {s.shotIntent && (
                  <p className="mt-0.5 truncate text-2xs italic text-muted-foreground">
                    {s.shotIntent}
                  </p>
                )}
                {/* W4:该镜绑定的卖点(factRefs)绿标 —— 「这一镜在讲哪个卖点」一眼可见。 */}
                {factChip && (
                  <span className="mt-0.5 inline-block max-w-full truncate rounded-full border border-emerald-300/50 bg-emerald-50 px-1.5 py-px text-2xs text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-950/30 dark:text-emerald-300">
                    {factChip}
                  </span>
                )}
                {lens && <p className="mt-0.5 truncate text-2xs text-primary/85">{lens}</p>}
              </div>
            </button>
          );
        })}
        {onAddShot && (
          <button
            type="button"
            onClick={onAddShot}
            className="flex min-h-[130px] cursor-pointer items-center justify-center rounded-[10px] border border-dashed border-border/70 text-[12.5px] text-muted-foreground transition-colors hover:border-primary/60 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            ＋ 加一镜
          </button>
        )}
      </div>
    </div>
  );
}
